// src/components/SimpleFormComponents/SimpleSelect.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Button, Divider, Flex, Select, Typography, Form } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useSimpleFormContext } from './SimpleFormProvider';
import { useModuleContext } from 'components/core/ModuleProvider';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const EMPTY_CONTENT_STYLE = {
  textAlign: 'center',
  padding: '8px 0',
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleSelect = ({
  name,
  subdocumentField,
  style = {},
  styleItem = {},
  options = [],
  showSearch = true,
  allowClear = true,
  showAddButton = false,
  onAddButtonLabel = 'new',
  useCustomLabel = false,
  onAddButtonFn = () => {},
  disabled = false,
  placeholder,
  onChange,
  onBlur,
  validateTrigger = 'onBlur',
  showFeedback = false,
  required: requiredProp,
  // Select-specific props (extracted to avoid passing to DOM)
  onSearch,
  onSelect: onSelectProp,
  onClear: onClearProp,
  filterOption,
  loading,
  mode,
  maxTagCount,
  maxTagPlaceholder,
  tokenSeparators,
  dropdownRender,
  optionRender,
  labelRender,
  tagRender,
  notFoundContent,
  popupClassNames,
  suffixIcon,
  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, disabledFields, shouldShowErrors, requiredFields, form } = useSimpleFormContext();
  const { openParametersModal, documentDuplicated } = useModuleContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;

  // Subscribe to language changes via Zustand - triggers re-render when language changes
  const storedLanguage = useZustand('theme', 'language');

  // PERFORMANCE FIX: Track last value to avoid unnecessary onChange calls
  const lastValueRef = useRef(null);
  const selectDebounceTimerRef = useRef(null);

  // Watch field value for validation
  const fieldValue = Form.useWatch(fullFieldName, form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);
  const required = requiredProp ?? requiredFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // ANTD 6 FIX: Control dropdown open state to prevent re-render interference
  const [open, setOpen] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);
  const [customValidationStatus, setCustomValidationStatus] = useState('');
  const [customValidationMessage, setCustomValidationMessage] = useState('');

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (selectDebounceTimerRef.current) {
        clearTimeout(selectDebounceTimerRef.current);
      }
    };
  }, []);

  // Activate validation when form shows errors (after submit attempt)
  useEffect(() => {
    if (shouldShowErrors) setShouldValidate(true);
  }, [shouldShowErrors]);

  // Activate validation when document is duplicated
  useEffect(() => {
    if (documentDuplicated) setShouldValidate(true);
  }, [documentDuplicated]);

  // Validate field value
  useEffect(() => {
    if (!shouldValidate) return;

    // Check if value is empty (handles undefined, null, empty string, and empty array)
    const isEmpty =
      fieldValue === undefined ||
      fieldValue === null ||
      fieldValue === '' ||
      (Array.isArray(fieldValue) && fieldValue.length === 0);

    const hasValue = !isEmpty;

    if (!hasValue && required) {
      setCustomValidationStatus('error');
      setCustomValidationMessage(translateX('required_field'));
      return;
    }

    if (hasValue) {
      setCustomValidationStatus('success');
      setCustomValidationMessage('');
      return;
    }

    setCustomValidationStatus('');
    setCustomValidationMessage('');
  }, [fieldValue, required, shouldValidate]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  // ANTD 6 FIX: Explicitly close dropdown on select to avoid race condition with Form.useWatch
  // NOTE: Don't close for multiple/tags mode - user needs to select multiple items
  const handleSelect = useCallback((value, option) => {
    // Only close dropdown for single select mode
    if (!mode || (mode !== 'multiple' && mode !== 'tags')) {
      setOpen(false);
    }
    // Call custom onSelect if provided
    if (typeof onSelectProp === 'function') {
      onSelectProp(value, option);
    }
  }, [onSelectProp, mode]);

  const handleDropdownVisibleChange = useCallback(visible => {
    setOpen(visible);
  }, []);

  const handleChange = useCallback(
    (value, option) => {
      // PERFORMANCE FIX: Only call onChange if value actually changed
      if (value !== lastValueRef.current) {
        lastValueRef.current = value;

        // NOTE: Validation is NOT triggered on change - only on submit attempt
        // This prevents showing errors while user is still filling the form

        if (typeof onChange === 'function') {
          onChange(value, option);
        }
      }
    },
    [onChange]
  );

  const handleBlur = useCallback(
    ev => {
      // Clear debounce timer on blur
      if (selectDebounceTimerRef.current) {
        clearTimeout(selectDebounceTimerRef.current);
        selectDebounceTimerRef.current = null;
      }

      if (typeof onBlur === 'function') {
        // Defer blur-side effects to next macrotask to avoid swallowing immediate clicks
        setTimeout(() => {
          onBlur(ev);
        }, 0);
      }
    },
    [onBlur]
  );

  const handleClear = useCallback(() => {
    // NOTE: Validation is NOT triggered on clear - only on submit attempt
    // This prevents showing errors while user is still filling the form
    lastValueRef.current = null;

    if (typeof onClearProp === 'function') {
      onClearProp();
    }
  }, [onClearProp]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const translatedOptions = useMemo(
    () => (useCustomLabel ? options : buildOptions(options)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, storedLanguage, useCustomLabel]
  );

  // Custom empty content without icon
  const emptyContent = useMemo(
    () => (
      <div style={EMPTY_CONTENT_STYLE}>
        <Text type="secondary">{translateX('no_data')}</Text>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storedLanguage]
  );

  // Parse showAddButton config - can be boolean or object { modal: 'parameters', tab: 'units' }
  // Compute select style based on mode - multi-select needs minHeight to allow tag expansion
  const selectStyle = useMemo(() => {
    const isMultipleMode = mode === 'multiple' || mode === 'tags';
    return isMultipleMode
      ? { minHeight: 40, ...style }
      : { height: 40, ...style };
  }, [mode, style]);

  const addButtonConfig = useMemo(() => {
    if (!showAddButton) return null;

    // Boolean true - use default onAddButtonFn
    if (showAddButton === true) {
      return {
        show: true,
        onClick: onAddButtonFn,
        label: onAddButtonLabel,
      };
    }

    // Object config - e.g., { modal: 'parameters', tab: 'units' }
    if (typeof showAddButton === 'object') {
      const { modal, tab } = showAddButton;

      if (modal === 'parameters' && openParametersModal) {
        return {
          show: true,
          onClick: () => openParametersModal(tab || null),
          label: showAddButton.label || 'new',
        };
      }

      // Custom modal - use onAddButtonFn
      return {
        show: true,
        onClick: onAddButtonFn,
        label: showAddButton.label || onAddButtonLabel,
      };
    }

    return null;
  }, [showAddButton, onAddButtonFn, onAddButtonLabel, openParametersModal]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      style={styleItem}
      validateTrigger={validateTrigger}
      showFeedback={showFeedback}
      required={required}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
    >
      <Select
        key={storedLanguage} // force remount when language changes
        ref={elementRef}
        id={flattenFieldName}
        style={selectStyle}
        options={translatedOptions}
        // ANTD 6: filterOption - use custom if provided, otherwise default for showSearch
        filterOption={filterOption !== undefined ? filterOption : (showSearch ? defaultFilterOption : undefined)}
        optionFilterProp="label"
        showSearch={showSearch}
        allowClear={allowClear}
        disabled={isDisabled}
        placeholder={translateX(placeholder)}
        optionLabelProp="label"
        // Custom empty state without icon (use prop override if provided)
        notFoundContent={notFoundContent !== undefined ? notFoundContent : emptyContent}
        // Select-specific props
        suffixIcon={suffixIcon}
        loading={loading}
        mode={mode}
        maxTagCount={maxTagCount}
        maxTagPlaceholder={maxTagPlaceholder}
        tokenSeparators={tokenSeparators}
        onSearch={onSearch}
        onClear={handleClear}
        optionRender={optionRender}
        labelRender={labelRender}
        tagRender={tagRender}
        classNames={popupClassNames ? { popup: { root: popupClassNames } } : undefined}
        // ANTD 6 FIX: Controlled open state to prevent re-render interference
        open={open}
        onSelect={handleSelect}
        onOpenChange={handleDropdownVisibleChange}
        onChange={handleChange}
        onBlur={handleBlur}
        popupRender={
          addButtonConfig?.show
            ? menu => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Flex justify="center" align="center">
                    <Button
                      size="small"
                      style={{ width: '100%' }}
                      type="default"
                      className="m-2 pl-2 pr-3"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setOpen(false);
                        addButtonConfig.onClick();
                      }}
                    >
                      {translateX(addButtonConfig.label)}
                    </Button>
                  </Flex>
                </>
              )
            : undefined
        }
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleSelect;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function translateValue(maybeKey) {
  // translate only if the value is a string (treat as i18n key)
  return typeof maybeKey === 'string' ? translateX(maybeKey) : maybeKey;
}

function buildOptions(rawOptions = []) {
  // build AntD options preserving JSX labels; translate only string keys
  return (rawOptions ?? []).map(opt => {
    const labelFromKey = opt.labelKey ? translateX(opt.labelKey) : null;
    const textFromKey = opt.textKey ? translateX(opt.textKey) : null;

    const resolvedLabel = labelFromKey ?? translateValue(opt.label ?? opt.value);
    const resolvedText = textFromKey ?? translateValue(opt.text ?? opt.label ?? opt.value);

    // Destructure to remove labelKey and textKey from final options (not valid DOM attributes)
    const { labelKey, textKey, ...restOpt } = opt;

    return {
      ...restOpt,
      value: opt.value,
      label: resolvedLabel,
      text: resolvedText,
      disabled: !!opt.disabled,
    };
  });
}

function defaultFilterOption(input, option) {
  // Prefer `text` (always a searchable string) over `label` (may be JSX).
  // Falls back to `label` only when it is a string.
  const text =
    (typeof option?.text === 'string' && option.text) ||
    (typeof option?.label === 'string' && option.label) ||
    '';
  return text.toLowerCase().includes((input || '').toLowerCase());
}
